/**
 * Pre-Marital Assets Form Component
 */

import React from 'react';
import { FormData } from '../types';

interface PreMaritalFormProps {
  formData: FormData;
  onChange: (field: keyof FormData, value: any) => void;
  onNext: () => void;
  onBack: () => void;
}

const PreMaritalForm: React.FC<PreMaritalFormProps> = ({ formData, onChange, onNext, onBack }) => {
  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">
        Pre-Marital Assets
      </h2>

      <p className="text-gray-600 mb-6">
        Assets owned before the marriage are generally considered separate property
        and may not be subject to division.
      </p>

      <div className="space-y-6">
        {/* User Pre-Marital Assets */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Your Pre-Marital Assets Total
          </label>
          <div className="relative">
            <span className="absolute left-4 top-3 text-gray-500">$</span>
            <input
              type="number"
              min="0"
              value={formData.userPreMaritalAssets || 0}
              onChange={(e) => onChange('userPreMaritalAssets', Number(e.target.value))}
              className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="0"
            />
          </div>
          <p className="text-gray-500 text-sm mt-1">
            Total value of assets you owned before marriage
          </p>
        </div>

        {/* Spouse Pre-Marital Assets */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Spouse Pre-Marital Assets Total
          </label>
          <div className="relative">
            <span className="absolute left-4 top-3 text-gray-500">$</span>
            <input
              type="number"
              min="0"
              value={formData.spousePreMaritalAssets || 0}
              onChange={(e) => onChange('spousePreMaritalAssets', Number(e.target.value))}
              className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="0"
            />
          </div>
          <p className="text-gray-500 text-sm mt-1">
            Total value of assets your spouse owned before marriage
          </p>
        </div>

        {/* Information Box */}
        <div className="bg-green-50 p-4 rounded-lg">
          <h3 className="font-semibold text-green-900 mb-2">
            📌 What Are Pre-Marital Assets?
          </h3>
          <ul className="text-sm text-green-800 space-y-1">
            <li>• Property owned before marriage</li>
            <li>• Inheritances received before marriage</li>
            <li>• Gifts from third parties before marriage</li>
            <li>• Generally considered separate property</li>
            <li>• May be protected from division in some states</li>
          </ul>
        </div>

        {/* Important Note */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <h3 className="font-semibold text-yellow-800 mb-2">
            ⚠️ Important
          </h3>
          <p className="text-sm text-yellow-700">
            Laws vary by state regarding pre-marital assets. In community property states,
            assets acquired during marriage are generally subject to equal division.
            Consult with an attorney for state-specific guidance.
          </p>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="mt-8 flex justify-between">
        <button
          onClick={onBack}
          className="bg-gray-200 text-gray-700 font-semibold py-3 px-8 rounded-lg hover:bg-gray-300 transition duration-300"
        >
          Back
        </button>
        <button
          onClick={onNext}
          className="bg-blue-600 text-white font-semibold py-3 px-8 rounded-lg hover:bg-blue-700 transition duration-300"
        >
          Next: Special Factors
        </button>
      </div>
    </div>
  );
};

export default PreMaritalForm;